import { ExternalLinkIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AnalysisStatusBadge } from '../components/badges/analysis-status-badge.tsx'
import { TriageCompositionBar } from '../components/composition-bar.tsx'
import { FindingFixDrawer } from '../components/drawers/finding-fix-drawer.tsx'
import { FindingTriageDrawer } from '../components/drawers/finding-triage-drawer.tsx'
import { DropdownFilter } from '../components/dropdown-filter'
import { FindingsTable } from '../components/findings-table.tsx'
import { Spinner } from '../components/spinner'
import { Pagination } from '../components/table/table'
import { BranchName } from '../components/tags/branch-name.tsx'
import { TooltipWithHover } from '../components/tooltip'
import { utilities } from '../main.css.ts'
import { Routes } from '../routes'
import {
  useGetChangesetsForFix,
  useGetFinding,
  useGetFindingArticle,
  useGetFindingReport,
  useGetFindings,
  useGetFixesForFinding,
  useGetRepository,
  useGetScanAnalysis,
  usePostPatch,
} from '../utils/api-client/api-hooks.ts'
import { Changeset, Finding, Fix, PaginatedFindings, Repository, RepositoryType, ScanAnalysis, Tool } from '../utils/api-client/api-schemas.ts'
import { useTheme } from '../utils/higher-order-components/with-theme'
import { useAddToast } from '../utils/higher-order-components/with-toasts'
import { getRepositoryIcon } from '../utils/repository-utils'
import * as styles from './analysis-details-page.css'

interface AnalysisDetailsPageUIState {
  hasFix: boolean
  hasNoFix: boolean
  toggleHasFix: () => void
  toggleHasNoFix: () => void
  findingDetailsTab: 'summary' | 'full-report'
  setFindingDetailsTab: (tab: 'summary' | 'full-report') => void
}

export const useAnalysisDetailsPageUI = create<AnalysisDetailsPageUIState>()(
  persist(
    set => ({
      hasFix: false,
      hasNoFix: false,
      toggleHasFix: () => set(state => ({ hasFix: !state.hasFix })),
      toggleHasNoFix: () => set(state => ({ hasNoFix: !state.hasNoFix })),
      findingDetailsTab: 'summary',
      setFindingDetailsTab: (tab: 'summary' | 'full-report') => set({ findingDetailsTab: tab }),
    }),
    {
      name: 'analysis-details-page-ui-state',
    }
  )
)

export const AnalysisDetailsPage: React.FC<{}> = () => {
  useRedirectFromOldAnalysisDetailsPageRoute()
  useRedirectFromOldFindingFixDrawerRoute()

  const { analysisId, isFindingFixDrawerRoute, isFindingTriageDrawerRoute } = useAnalysisDetailsPageParams()
  const { scanAnalysis, repository } = useScanAnalysisAndRepositoryInfo()

  const { paginatedFindings, pagination, handlePaginationChange } = useFindingsTable()

  const {
    selectedFindingForFixPreviewDrawer,
    handleSetSelectedFindingIdForFindingFixDrawer,
    fixes,
    changesets,
    handleSendPatch,
    isSendPatchLoading,
  } = useFindingFixDrawer()

  const { selectedFindingForFindingTriageDrawer, findingTriageArticle, findingTriageReport, handleSetSelectedFindingIdForFindingTriageDrawer } =
    useFindingTriageDrawer()

  const { theme } = useTheme()

  return (
    <AnalysisDetailsPagePure
      // Core data
      scanAnalysis={scanAnalysis}
      repository={repository}
      // Findings table
      paginatedFindings={paginatedFindings}
      pagination={pagination}
      handlePaginationChange={handlePaginationChange}
      // Finding details drawer
      selectedFindingForFindingTriageDrawer={selectedFindingForFindingTriageDrawer}
      findingTriageArticle={findingTriageArticle}
      findingTriageReport={findingTriageReport}
      handleSetSelectedFindingIdForFindingTriageDrawer={handleSetSelectedFindingIdForFindingTriageDrawer}
      // Fix preview drawer
      selectedFindingForFixPreviewDrawer={selectedFindingForFixPreviewDrawer}
      handleSetSelectedFindingIdForFindingFixDrawer={handleSetSelectedFindingIdForFindingFixDrawer}
      fixes={fixes}
      changesets={changesets}
      handleSendPatch={handleSendPatch}
      isSendPatchLoading={isSendPatchLoading}
      // Theme
      theme={theme}
      // Route state
      analysisId={analysisId}
      isFindingFixDrawerRoute={isFindingFixDrawerRoute}
      isFindingTriageDrawerRoute={isFindingTriageDrawerRoute}
    />
  )
}

export interface AnalysisDetailsPagePureProps {
  // Core data
  scanAnalysis?: ScanAnalysis
  repository?: Repository

  // Findings table
  paginatedFindings?: PaginatedFindings
  pagination: Pagination
  handlePaginationChange: (pagination: Pagination) => void

  // Finding details drawer
  selectedFindingForFindingTriageDrawer?: Finding
  findingTriageArticle?: string
  findingTriageReport?: string
  handleSetSelectedFindingIdForFindingTriageDrawer: (findingId: string | undefined) => void

  // Fix preview drawer
  selectedFindingForFixPreviewDrawer?: Finding
  handleSetSelectedFindingIdForFindingFixDrawer: (findingId: string | undefined) => void
  fixes?: Fix[]
  changesets?: Changeset[]
  handleSendPatch: () => void
  isSendPatchLoading: boolean

  // Theme
  theme: ReturnType<typeof useTheme>['theme']

  // Route state
  analysisId: string
  isFindingFixDrawerRoute: boolean
  isFindingTriageDrawerRoute: boolean
}
export const AnalysisDetailsPagePure: React.FC<AnalysisDetailsPagePureProps> = ({
  // Core data
  scanAnalysis,
  repository,
  // Findings table
  paginatedFindings,
  pagination,
  handlePaginationChange,
  // Finding details drawer
  selectedFindingForFindingTriageDrawer,
  findingTriageArticle,
  findingTriageReport,
  handleSetSelectedFindingIdForFindingTriageDrawer,
  // Fix preview drawer
  selectedFindingForFixPreviewDrawer,
  handleSetSelectedFindingIdForFindingFixDrawer,
  fixes,
  changesets,
  handleSendPatch,
  isSendPatchLoading,
  // Theme
  theme,
  // Route state
  analysisId,
  isFindingFixDrawerRoute,
  isFindingTriageDrawerRoute,
}) => {
  return (
    <div className={styles.analysisDetailsContainer}>
      <nav aria-label="Breadcrumb to All Scans">
        <BreadcrumbContent />
      </nav>
      <header className={styles.analysisDetailsHeader} role="banner">
        <ScanInfoSection scanAnalysis={scanAnalysis} theme={theme} />
        <ScanCompositionSection scanAnalysis={scanAnalysis} repository={repository} paginatedFindings={paginatedFindings} />
        <MetricsSection scanAnalysis={scanAnalysis} paginatedFindings={paginatedFindings} theme={theme} />
      </header>
      {isFindingTriageDrawerRoute && selectedFindingForFindingTriageDrawer && (
        <FindingTriageDrawer
          analysisId={analysisId}
          findingId={selectedFindingForFindingTriageDrawer.id}
          finding={selectedFindingForFindingTriageDrawer}
          triageArticle={findingTriageArticle}
          triageReport={findingTriageReport}
          handleClose={() => handleSetSelectedFindingIdForFindingTriageDrawer(undefined)}
          theme={theme}
        />
      )}
      {isFindingFixDrawerRoute && selectedFindingForFixPreviewDrawer && (
        <FindingFixDrawer
          analysisId={analysisId}
          findingId={selectedFindingForFixPreviewDrawer.id}
          fix={fixes?.[0]}
          changesets={changesets}
          patch={selectedFindingForFixPreviewDrawer._embedded?.['latest-patch'] ?? undefined}
          findingTriageResult={selectedFindingForFixPreviewDrawer._embedded?.triage?.result}
          repositoryType={repository?.type}
          isSendPatchLoading={isSendPatchLoading}
          handleSendPatch={handleSendPatch}
          onClose={() => handleSetSelectedFindingIdForFindingFixDrawer(undefined)}
          theme={theme}
        />
      )}
      <TableControls />
      <FindingsTable
        paginatedFindings={paginatedFindings}
        pagination={pagination}
        handlePaginationChange={handlePaginationChange}
        selectedFindingId={selectedFindingForFindingTriageDrawer?.id || selectedFindingForFixPreviewDrawer?.id}
        handleSetSelectedFindingIdForFindingTriageDrawer={handleSetSelectedFindingIdForFindingTriageDrawer}
        handleSetSelectedFindingIdForFindingFixDrawer={handleSetSelectedFindingIdForFindingFixDrawer}
      />
    </div>
  )
}

interface BreadcrumbContentProps {}
const BreadcrumbContent: React.FC<BreadcrumbContentProps> = () => {
  return (
    <div className={styles.breadcrumbContainer}>
      <Link to={Routes.ScansOverviewPage.path} className={styles.breadcrumbLink}>
        All scans
      </Link>{' '}
      <span className={styles.breadcrumbSeparator}>/ Scan analysis</span>
    </div>
  )
}

interface ScanInfoSectionProps {
  scanAnalysis?: ScanAnalysis
  theme: 'light' | 'dark'
}
const ScanInfoSection: React.FC<ScanInfoSectionProps> = ({ scanAnalysis, theme }) => {
  return (
    <section className={styles.scanInfoContainer}>
      {scanAnalysis ? <ScanInfo scanAnalysis={scanAnalysis} theme={theme} /> : <Spinner label="Loading scan analysis..." />}
    </section>
  )
}

interface ScanCompositionSectionProps {
  scanAnalysis?: ScanAnalysis
  repository?: Repository
  paginatedFindings?: PaginatedFindings
}
const ScanCompositionSection: React.FC<ScanCompositionSectionProps> = ({ scanAnalysis, repository, paginatedFindings }) => {
  return (
    <section className={styles.scanCompositionContainer} aria-labelledby="scan-composition-header">
      <h2 id="scan-composition-header" className={utilities.visuallyHidden}>
        Scan Composition
      </h2>
      {scanAnalysis && repository && <RepositoryInformation repository={repository} analysisState={scanAnalysis.current_state.state} />}
      {scanAnalysis?._embedded.scan.detector && paginatedFindings ? (
        <TriageCompositionBar
          fixes={paginatedFindings.fixes}
          total={paginatedFindings.total}
          falsePositives={paginatedFindings.false_positives}
          truePositives={paginatedFindings.true_positives}
          suspicious={paginatedFindings.suspicious}
          wontFix={paginatedFindings.wont_fix}
          tool={scanAnalysis._embedded.scan.detector}
          analysisState={scanAnalysis.current_state.state}
        />
      ) : (
        <Spinner label="Loading scan details and findings." />
      )}
    </section>
  )
}

interface MetricsSectionProps {
  scanAnalysis?: ScanAnalysis
  paginatedFindings?: PaginatedFindings
  theme: 'light' | 'dark'
}
export const MetricsSection: React.FC<MetricsSectionProps> = ({ scanAnalysis, paginatedFindings, theme }) => {
  const analysisState = scanAnalysis?.current_state?.state
  const isCompletedState = analysisState === 'completed'
  const shouldShowMetrics = isCompletedState && paginatedFindings

  let totalFindings = 0
  let totalTriaged = 0
  let totalFixes = 0
  if (paginatedFindings) {
    totalFindings = paginatedFindings.total
    totalTriaged = paginatedFindings.true_positives + paginatedFindings.suspicious + paginatedFindings.wont_fix + paginatedFindings.false_positives
    totalFixes = Math.min(paginatedFindings.fixes, totalFindings)
  }

  const fixCoverageValue = match(totalFindings)
    .with(0, () => {
      return '0%'
    })
    .otherwise(() => {
      return `${Math.round((totalFixes / totalFindings) * 100)}%`
    })

  const triagedValue = match(totalFindings)
    .with(0, () => {
      return '0%'
    })
    .otherwise(() => {
      return `${Math.round((totalTriaged / totalFindings) * 100)}%`
    })

  return (
    <section className={styles.metricsSection} role="complementary" aria-labelledby="metrics-header">
      <h2 id="metrics-header" className={utilities.visuallyHidden}>
        Metrics and External Link
      </h2>
      {scanAnalysis?._embedded?.scan?.html_url && (
        <a href={scanAnalysis._embedded.scan.html_url} target="_blank" rel="noopener noreferrer" className={styles.toolLink}>
          View in {mapToolToLogoAndName(scanAnalysis._embedded.scan.detector, theme).name}
          <ExternalLinkIcon className={styles.externalLinkIcon} />
        </a>
      )}
      <div className={styles.metricsContainer} role="group" aria-label="Findings Metrics">
        <output id="findings-count" className={styles.metricValue}>
          {shouldShowMetrics ? totalFindings : '-'}
        </output>
        <label htmlFor="findings-count" className={styles.metricLabel}>
          findings
        </label>
        <output id="triaged" className={styles.metricValue}>
          {shouldShowMetrics ? triagedValue : '-'}
        </output>
        <label htmlFor="triaged" className={styles.metricLabel}>
          triaged
        </label>
        <output id="fix-coverage" className={styles.metricValue}>
          {shouldShowMetrics ? fixCoverageValue : '-'}
        </output>
        <label htmlFor="fix-coverage" className={styles.metricLabel}>
          fix coverage
        </label>
      </div>
    </section>
  )
}

interface AnalysisLog {
  state: string
  timestamp: string
}

export const getDuration = (currentState: string, logs: AnalysisLog[]): string | null => {
  if (currentState !== 'completed') return null

  const completedLog = logs.find(log => log.state === 'completed')
  const inProgressLog = logs.find(log => log.state === 'in_progress')

  if (!completedLog || !inProgressLog) return null

  const completedTime = DateTime.fromISO(completedLog.timestamp)
  const startTime = DateTime.fromISO(inProgressLog.timestamp)

  const diff = completedTime.diff(startTime, ['minutes', 'seconds'])
  const minutes = Math.floor(diff.minutes)
  const seconds = Math.floor(diff.seconds)

  return minutes > 0 ? `${minutes}m${seconds}s` : `${seconds}s`
}

interface ScanInfoProps {
  scanAnalysis: ScanAnalysis
  theme: 'light' | 'dark'
}
const ScanInfo: React.FC<ScanInfoProps> = ({ scanAnalysis, theme }) => {
  const { name, logoHref } = mapToolToLogoAndName(scanAnalysis._embedded.scan.detector, theme)
  const scanDate = DateTime.fromISO(scanAnalysis._embedded.scan.imported_at)

  const duration = getDuration(scanAnalysis.current_state.state, scanAnalysis.log)

  return (
    <>
      <h2 className={utilities.visuallyHidden}>Scan Information</h2>
      <div className={styles.toolLogoContainer}>
        <img src={logoHref} alt={`${name} logo`} className={styles.toolLogo} />
      </div>
      <div className={styles.dateContainer}>
        <p className={styles.dateText}>{scanDate.toFormat('M/d/yy')}</p>
        <p className={styles.timeText}>
          {scanDate.toFormat('hh:mm:ss a')} {scanDate.toFormat('ZZZZ')}
        </p>
        {duration && <p className={styles.durationText}>Duration: {duration}</p>}
      </div>
      {scanAnalysis.branch && <BranchName branch={scanAnalysis.branch} />}
    </>
  )
}

interface RepositoryInformationProps {
  repository: Repository
  analysisState?: ScanAnalysis['current_state']['state']
}
const RepositoryInformation: React.FC<RepositoryInformationProps> = ({ repository, analysisState }) => {
  const displayName = repositoryNameToDisplay(repository.type, repository.name)
  const shouldTruncate = displayName.name.length > 85
  const isCompletedState = analysisState === 'completed'

  return (
    <>
      {'owner' in displayName && <p className={styles.accountLoginText}>{displayName.owner}</p>}
      <div className={styles.repositoryNameContainer}>
        <div className={styles.repositoryNameSection}>
          {getRepositoryIcon({ type: repository.type, className: styles.scmIcon })}
          {shouldTruncate ? (
            <TooltipWithHover trigger={<p className={styles.repositoryName}>{`${displayName.name.slice(0, 85)}...`}</p>}>
              <span>{displayName.name}</span>
            </TooltipWithHover>
          ) : (
            <p className={styles.repositoryName}>{displayName.name}</p>
          )}
        </div>
        {analysisState && !isCompletedState && <AnalysisStatusBadge variant={analysisState} />}
      </div>
    </>
  )
}

const TableControls: React.FC<{}> = () => {
  const hasFix = useAnalysisDetailsPageUI(state => state.hasFix)
  const hasNoFix = useAnalysisDetailsPageUI(state => state.hasNoFix)
  const handleToggleHasFix = useAnalysisDetailsPageUI(state => state.toggleHasFix)
  const handleToggleHasNoFix = useAnalysisDetailsPageUI(state => state.toggleHasNoFix)

  return (
    <div className={styles.tableControls}>
      <DropdownFilter
        filterGroups={[
          {
            id: 'availableFix',
            text: 'Available Fix',
            filters: [
              { id: 'hasFix', text: 'Yes', value: hasFix, handleToggle: handleToggleHasFix },
              { id: 'hasNoFix', text: 'No', value: hasNoFix, handleToggle: handleToggleHasNoFix },
            ],
          },
        ]}
      />
    </div>
  )
}

export const mapToolToLogoAndName = (tool: Tool, mode: 'light' | 'dark') => {
  const suffix = mode === 'light' ? 'Lt' : 'Dk'
  return match(tool)
    .with('APPSCAN', () => ({ name: 'AppScan', logoHref: '/SastToolVisualAssets/AppScan-Icon.png' }))
    .with('POLARIS', () => ({ name: 'Black Duck Polaris', logoHref: `/SastToolVisualAssets/Polaris-Icon.svg` }))
    .with('CHECKMARX', () => ({ name: 'Checkmarx', logoHref: '/SastToolVisualAssets/Checkmarx-Icon.svg' }))
    .with('CODEQL', () => ({ name: 'CodeQL', logoHref: '/SastToolVisualAssets/CodeQL-Logo.svg' }))
    .with('CONTRAST', () => ({ name: 'Contrast', logoHref: `/SastToolVisualAssets/Contrast-Icon-${suffix}.svg` }))
    .with('DEFECT_DOJO', () => ({ name: 'DefectDojo', logoHref: '/SastToolVisualAssets/DefectDojo-Icon.svg' }))
    .with('PIXEE', () => ({ name: 'Pixee', logoHref: '/SastToolVisualAssets/Pixee-Icon.svg' }))
    .with('SEMGREP', () => ({ name: 'Semgrep', logoHref: '/SastToolVisualAssets/Semgrep-Icon.svg' }))
    .with('SNYK', () => ({ name: 'Snyk', logoHref: `/SastToolVisualAssets/Snyk-Icon-${suffix}.svg` }))
    .with('SONAR', () => ({ name: 'SonarQube', logoHref: `/SastToolVisualAssets/Sonar-Icon-${suffix}.svg` }))
    .with('VERACODE', () => ({ name: 'Veracode', logoHref: `/SastToolVisualAssets/Veracode-Icon-${suffix}.svg` }))
    .exhaustive()
}

export const repositoryNameToDisplay = (type: RepositoryType, repositoryName: string): { owner: string; name: string } | { name: string } => {
  if (type === 'github') {
    const [owner, name] = repositoryName.split('/')
    invariant(owner, 'Owner is required for GitHub repository names')
    invariant(name, 'Name is required for GitHub repository names')
    return { owner, name }
  }

  const name = repositoryName
  invariant(name, 'Name is required for generic Git repository names')
  return { name }
}

function useAnalysisDetailsPageParams() {
  const { analysisId, findingId } = useParams()
  const isFindingFixDrawerRoute = Routes.AnalysisDetailsPageFindingFixDrawer.isRoute(location.pathname)
  const isFindingTriageDrawerRoute = Routes.AnalysisDetailsPageFindingTriageDrawer.isRoute(location.pathname)

  invariant(analysisId, 'Missing required parameter: analysisId')

  if (isFindingFixDrawerRoute) invariant(findingId, 'Missing required parameter for finding fix drawer route: findingId')

  if (isFindingTriageDrawerRoute) invariant(findingId, 'Missing required parameter for finding triage drawer route: findingId')

  return useMemo(
    () => ({
      analysisId,
      findingId,
      isFindingFixDrawerRoute,
      isFindingTriageDrawerRoute,
    }),
    [analysisId, findingId, isFindingFixDrawerRoute, isFindingTriageDrawerRoute]
  )
}

export function useRedirectFromOldAnalysisDetailsPageRoute() {
  const { analysisId } = useAnalysisDetailsPageParams()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const scanId = location.pathname.split('/').pop()
    if (scanId && location.pathname.includes(`/analysis/${analysisId}/${scanId}`)) {
      navigate(Routes.AnalysisDetailsPage.createPath({ analysisId }), { replace: true })
    }
  }, [analysisId, location.pathname, navigate])
}

export function useRedirectFromOldFindingFixDrawerRoute() {
  const { analysisId, findingId } = useAnalysisDetailsPageParams()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (location.pathname.includes(`/analysis/${analysisId}/fix/${findingId}`)) {
      invariant(findingId, 'Finding ID path param is required for redirect from old preview fix drawer route')
      navigate(Routes.AnalysisDetailsPageFindingFixDrawer.createPath({ analysisId, findingId }), { replace: true })
    }
  }, [analysisId, findingId, location.pathname, navigate])
}

function usePagination() {
  const defaultPageIndex = 0
  const defaultPageSize = 50
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const pageIndex = Number(searchParams.get('page_number') ?? defaultPageIndex)
  const pageSize = Number(searchParams.get('page_size') ?? defaultPageSize)

  // If we're at default values, remove the parameters from URL
  useEffect(() => {
    if (pageIndex === defaultPageIndex && pageSize === defaultPageSize && (searchParams.has('page_number') || searchParams.has('page_size'))) {
      navigate('', { replace: true })
    }
  }, [pageIndex, pageSize, defaultPageIndex, defaultPageSize, navigate, searchParams])

  const pagination = { pageIndex, pageSize }

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      if (pagination.pageIndex === defaultPageIndex && pagination.pageSize === defaultPageSize) {
        navigate('')
        return
      }

      const params = new URLSearchParams({
        page_number: pagination.pageIndex.toString(),
        page_size: pagination.pageSize.toString(),
      })

      navigate(`?${params.toString()}`)
    },
    [defaultPageIndex, defaultPageSize, navigate]
  )

  return {
    pagination,
    handlePaginationChange,
  }
}

function useScanAnalysisAndRepositoryInfo() {
  const { analysisId } = useAnalysisDetailsPageParams()

  const { data: scanAnalysis } = useGetScanAnalysis({
    analysisId,
  })

  const { data: repository } = useGetRepository({
    path: scanAnalysis?._embedded?.scan?._links?.repository?.href!,
    enabled: !!scanAnalysis?._embedded?.scan?._links?.repository?.href,
  })

  return useMemo(
    () => ({
      scanAnalysis,
      repository,
    }),
    [scanAnalysis, repository]
  )
}

function useFindingsTable() {
  const { analysisId } = useAnalysisDetailsPageParams()
  const { pagination, handlePaginationChange } = usePagination()

  const hasFix = useAnalysisDetailsPageUI(state => state.hasFix)
  const hasNoFix = useAnalysisDetailsPageUI(state => state.hasNoFix)

  const { data: paginatedFindings } = useGetFindings({
    analysisId,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    hasFix,
    hasNoFix,
  })

  return useMemo(
    () => ({
      paginatedFindings,
      pagination,
      handlePaginationChange,
    }),
    [paginatedFindings, pagination, handlePaginationChange]
  )
}

function useFindingTriageDrawer() {
  const { analysisId, findingId, isFindingTriageDrawerRoute } = useAnalysisDetailsPageParams()
  const navigate = useNavigate()

  const findingDetailsTab = useAnalysisDetailsPageUI(state => state.findingDetailsTab)

  const { data: selectedFindingForFindingTriageDrawer } = useGetFinding({
    analysisId,
    findingId: findingId!,
    enabled: isFindingTriageDrawerRoute,
  })

  const { handleAddToastWithTimeout } = useAddToast()

  const {
    data: findingTriageArticle,
    error: findingArticleError,
    isError: isFindingArticleError,
  } = useGetFindingArticle({
    analysisId,
    findingId: findingId!,
    enabled: isFindingTriageDrawerRoute && findingDetailsTab === 'summary',
  })

  useEffect(() => {
    if (isFindingArticleError) {
      navigate(Routes.AnalysisDetailsPage.createPath({ analysisId }), { replace: true })
      handleAddToastWithTimeout({
        message: <p>Failed loading finding triage article: {findingArticleError?.message ?? 'Unknown error'}</p>,
        variant: 'error',
      })
      if (findingArticleError?.bodyAsText) {
        handleAddToastWithTimeout({
          message: <pre>{findingArticleError.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    }
  }, [isFindingArticleError, findingArticleError])

  const {
    data: findingTriageReport,
    error: findingReportError,
    isError: isFindingReportError,
  } = useGetFindingReport({
    analysisId,
    findingId: findingId!,
    enabled: isFindingTriageDrawerRoute && findingDetailsTab === 'full-report',
  })

  useEffect(() => {
    if (isFindingReportError) {
      navigate(Routes.AnalysisDetailsPage.createPath({ analysisId }), { replace: true })
      handleAddToastWithTimeout({
        message: <p>Failed loading finding triage report: {findingReportError?.message ?? 'Unknown error'}</p>,
        variant: 'error',
      })
      if (findingReportError?.bodyAsText) {
        handleAddToastWithTimeout({
          message: <pre>{findingReportError.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    }
  }, [isFindingReportError, findingReportError])

  const handleSetSelectedFindingIdForFindingTriageDrawer = useCallback(
    (findingId: string | undefined) => {
      const currentQueryString = window.location.search.slice(1)

      const navigateTo =
        findingId === undefined
          ? Routes.AnalysisDetailsPage.createPath({ analysisId, queryString: currentQueryString })
          : Routes.AnalysisDetailsPageFindingTriageDrawer.createPath({
              analysisId,
              findingId,
              queryString: currentQueryString,
            })

      navigate(navigateTo, { replace: true })
    },
    [analysisId, navigate]
  )

  return useMemo(
    () => ({
      selectedFindingForFindingTriageDrawer,
      findingTriageArticle,
      findingTriageReport,
      handleSetSelectedFindingIdForFindingTriageDrawer,
    }),
    [selectedFindingForFindingTriageDrawer, findingTriageArticle, findingTriageReport, handleSetSelectedFindingIdForFindingTriageDrawer]
  )
}

function useFindingFixDrawer() {
  const { analysisId, findingId, isFindingFixDrawerRoute } = useAnalysisDetailsPageParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { handleAddToastWithTimeout } = useAddToast()

  const queryClient = useQueryClient()

  const { pagination } = usePagination()

  const { data: selectedFindingForFixPreviewDrawer } = useGetFinding({
    analysisId,
    findingId: findingId!,
    enabled: isFindingFixDrawerRoute,
  })

  const { data: paginatedFixes } = useGetFixesForFinding({
    analysisId,
    findingId: findingId!,
    enabled: isFindingFixDrawerRoute,
  })
  const fixes = paginatedFixes?._embedded?.items
  const fix = fixes?.[0]

  const { data: paginatedChangesets } = useGetChangesetsForFix({
    fixId: fix?.id!,
    enabled: !!fix,
  })
  const changesets = paginatedChangesets?._embedded?.items

  const postPatchMutation = usePostPatch({
    onSuccess: () => {
      handleAddToastWithTimeout({
        message: <>Request to send patch received successfully!</>,
        variant: 'success',
      })
      queryClient.invalidateQueries({
        queryKey: ['hal-findings', analysisId, pagination.pageIndex, pagination.pageSize],
      })
      queryClient.invalidateQueries({
        queryKey: ['finding', analysisId, findingId],
      })
      navigate(Routes.AnalysisDetailsPage.createPath({ analysisId, queryString: searchParams.toString() }), {
        replace: true,
      })
    },
    onError: () => {
      handleAddToastWithTimeout({
        message: <>Failed to send patch.</>,
        variant: 'error',
      })
    },
  })

  const handleSetSelectedFindingIdForFindingFixDrawer = useCallback(
    (findingId: string | undefined) => {
      const currentQueryString = window.location.search.slice(1)

      const navigateTo =
        findingId === undefined
          ? Routes.AnalysisDetailsPage.createPath({ analysisId, queryString: currentQueryString })
          : Routes.AnalysisDetailsPageFindingFixDrawer.createPath({
              analysisId,
              findingId,
              queryString: currentQueryString,
            })

      navigate(navigateTo, { replace: true })
    },
    [analysisId, navigate]
  )

  const handleSendPatch = useCallback(
    () =>
      postPatchMutation.mutate({
        analysisId,
        createPatchRequest: {
          findings: [findingId!],
        },
      }),
    [analysisId, findingId, postPatchMutation]
  )

  return useMemo(
    () => ({
      selectedFindingForFixPreviewDrawer,
      fixes,
      changesets,
      handleSetSelectedFindingIdForFindingFixDrawer,
      handleSendPatch,
      isSendPatchLoading: postPatchMutation.isPending,
    }),
    [
      selectedFindingForFixPreviewDrawer,
      fixes,
      changesets,
      handleSetSelectedFindingIdForFindingFixDrawer,
      handleSendPatch,
      postPatchMutation.isPending,
    ]
  )
}
